import React from "react";
import Constants from "../../../constants/Index";
import { LegendRounded } from "../LegendRounded/Index";
import { ButtonWithChildren } from "../../Buttons/ButtonWithChildren/Index";
import { useTranslation } from "react-i18next";

type ManagementLegendProps = {
  view: "type" | "height" | "rate" | "day";
  maxValueOnGradiente: number;
  maxOcupacao: number;
  maxDescanso: number;
  rateSelected: number;
  onRateChange: (rate: number) => void;
};

export function ManagementLegend({ 
  view, 
  maxValueOnGradiente, 
  maxOcupacao, 
  maxDescanso, 
  rateSelected, 
  onRateChange 
}: ManagementLegendProps) {
  const { t, i18n } = useTranslation();
  
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(num);
  };
  
  if (view === "type") {
    return (
      <section className="pasturemanagement__footerlegend--container">
        <h6>{t("mapLegend.management")}</h6>
        <div className="pasturemanagement__footerlegend--content">
          <LegendRounded label={t("mapLegend.continuous")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.ORANGE} />
          <LegendRounded label={t("mapLegend.rotational")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.RED} />
          <LegendRounded label={t("mapLegend.strip")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.BLUE} />
          <LegendRounded label={t("mapLegend.noMonitoring")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.WHITE} />
        </div>
      </section>
    );
  }

  if (view === "height") {
    return (
      <section className="pasturemanagement__footerlegend--container">
        {maxValueOnGradiente !== 0 && (
          <>
            <h6>{t("mapLegend.height")}</h6>
            <div className="pasturemanagement__footerlegend--content">
              <div>
                <div className="pasturemanagement--bar" />
                <div className="pasturemanagement--containerlegend">
                  <span>0</span>
                  <span>{formatNumber(Number(maxValueOnGradiente))}</span>
                </div>
              </div>
            </div>
            <br />
          </>
        )}
        <div className="pasturemanagement__footerlegend--content">
          <LegendRounded label={t("mapLegend.aboveHeight")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.GREEN} />
          <LegendRounded label={t("mapLegend.withinRange")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.CYAN} />
          <LegendRounded label={t("mapLegend.belowHeight")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.RED} />
          <LegendRounded label={t("mapLegend.noHeight")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.YELLOW} />
        </div>
      </section>
    );
  }

  if (view === "rate") {
    return (
      <>
        {maxValueOnGradiente !== 0 && (
          <section className="filterpolygons__container pasturemanagement__toplegend--container">
            <h6>{t("mapLegend.referenceRating")}</h6>
            <div>
              <ButtonWithChildren 
                theme={rateSelected === 0 ? "info" : "dark"} 
                onClick={() => onRateChange(0)}
              >
                0 a 5
              </ButtonWithChildren>
              <ButtonWithChildren 
                theme={rateSelected === 1 ? "info" : "dark"} 
                onClick={() => onRateChange(1)}
              >
                0 a 10
              </ButtonWithChildren>
            </div>
          </section>
        )}

        <section className="pasturemanagement__footerlegend--container">
          {maxValueOnGradiente !== 0 && (
            <>
              <h6>{t("mapLegend.rating")}</h6>
              <div className="pasturemanagement__footerlegend--content">
                <div>
                  <div className="pasturemanagement--bar" />
                  <div className="pasturemanagement--containerlegend">
                    <span>0</span>
                    <span>{formatNumber(Number(maxValueOnGradiente))}</span>
                  </div>
                </div>
              </div>
              <br />
            </>
          )}
          <div className="pasturemanagement__footerlegend--content">
            <LegendRounded label={t("mapLegend.aboveRating")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.GREEN} />
            <LegendRounded label={t("mapLegend.withinRange")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.CYAN} />
            <LegendRounded label={t("mapLegend.belowRating")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.RED} />
            <LegendRounded label={t("mapLegend.noRating")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.YELLOW} />
          </div>
        </section>
      </>
    );
  }

  if (view === "day") {
    return (
      <section className="pasturemanagement__footerlegend--container">
        {maxOcupacao !== 0 && (
          <div>
            <strong className="capacitybvroute__barnumber--title">{t("mapLegend.occupationDays")}</strong>
            <div className="paddocktime__barnumber--bar1" />
            <div className="paddocktime__barnumber--containerlegend">
              <span>0</span>
              <span>{formatNumber(Number(maxOcupacao))}</span>
            </div>
          </div>
        )}
        <br />
        {maxDescanso !== 0 && (
          <div>
            <strong className="capacitybvroute__barnumber--title">{t("mapLegend.restDays")}</strong>
            <div className="paddocktime__barnumber--bar2" />
            <div className="paddocktime__barnumber--containerlegend">
              <span>0</span>
              <span>{formatNumber(Number(maxDescanso))}</span>
            </div>
          </div>
        )}
        <br />
        <div className="pasturemanagement__footerlegend--content">
          <LegendRounded label={t("mapLegend.aboveOccupationDays")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.RED} />
          <LegendRounded label={t("mapLegend.belowOccupationDays")} bgColor={Constants.PASTUREMANAGEMENT_COLORS.GREEN} />
        </div>
      </section>
    );
  }

  return null;
}
